// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Save, ChevronDown, ChevronRight, FileText, Edit3, PlusCircle, Layers, CornerDownRight, Globe, Lock, RotateCcw } from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import logger from '../../utils/logger';
import './ChartOfAccountsEditor.css';

// Maximum nesting depth for subcategories
const MAX_NESTING_DEPTH = 5;

// Fixed P&L sections - these are always present
const PL_SECTIONS = [
  { 
    key: 'revenue', 
    name: 'Revenue', 
    description: 'Income from operations',
    // Default to no subcategories; the section name itself is used as the mapping category.
    // This avoids the invalid/ambiguous state: Revenue -> [Revenue]
    defaultCategories: []
  },
  { 
    key: 'cogs', 
    name: 'Cost of Goods Sold', 
    description: 'Direct costs of products/services',
    // Default to no subcategories; the section name itself is used as the mapping category.
    defaultCategories: []
  },
  { 
    key: 'opex', 
    name: 'Operating Expenses', 
    description: 'Day-to-day business expenses',
    // Default to no subcategories; the section name itself is used as the mapping category.
    defaultCategories: []
  },
  { 
    key: 'other_expenses', 
    name: 'Other Expenses', 
    description: 'Non-operating expenses',
    // Default to no subcategories; the section name itself is used as the mapping category.
    defaultCategories: []
  },
  { 
    key: 'other_income', 
    name: 'Other Income', 
    description: 'Non-operating income',
    // Default to no subcategories; the section name itself is used as the mapping category.
    defaultCategories: []
  },
];

// Below EBITDA sections (usually just need slots, not subcategories)
// These are fixed sections that are always included in every P&L template
// Note: Net Income is NOT listed here - it's a verification category that goes
// directly to the mapped_net_income_row for reconciliation against calculated Net Income
const BELOW_EBITDA_SECTIONS = [
  { key: 'amortization', name: 'Amortization', slots: 1 },
  { key: 'depreciation', name: 'Depreciation', slots: 1 },
  { key: 'interest', name: 'Interest Expense', slots: 1 },
  { key: 'tax', name: 'Corporate Tax', slots: 1 },
];

const generateId = () => String(Date.now()) + String(Math.random()).slice(2, 6);

// Generate default section structure
const getDefaultSectionStructure = () => {
  const structure = {};
  PL_SECTIONS.forEach(section => {
    structure[section.key] = {
      name: section.name,
      categories: section.defaultCategories.map((cat, idx) => ({
        id: `${section.key}-${idx}-${Date.now()}`,
        name: cat,
        children: []
      }))
    };
  });
  BELOW_EBITDA_SECTIONS.forEach(section => {
    structure[section.key] = {
      name: section.name
    };
  });
  return structure;
};

// Recursively convert stored subcategories to editor format
const convertStoredToEditor = (subcategories, prefix, depth = 0) => {
  if (!subcategories || !Array.isArray(subcategories) || depth >= MAX_NESTING_DEPTH) {
    return [];
  }
  
  return subcategories.map((sub, idx) => ({
    id: `${prefix}-${idx}-${Date.now()}`,
    name: sub.name || '',
    children: convertStoredToEditor(sub.subcategories, `${prefix}-${idx}`, depth + 1)
  }));
};

// Recursively convert editor format to storage format
const convertEditorToStorage = (categories, depth = 0) => {
  if (!categories || !Array.isArray(categories) || depth >= MAX_NESTING_DEPTH) {
    return [];
  }
  
  return categories
    .filter(cat => cat.name.trim())
    .map(cat => {
      const result = { name: cat.name.trim() };
      if (cat.children && cat.children.length > 0) {
        const childSubs = convertEditorToStorage(cat.children, depth + 1);
        if (childSubs.length > 0) {
          result.subcategories = childSubs;
        }
      }
      return result;
    });
};

// Recursively collect all category names (both parent and leaf)
const collectAllCategoryNames = (categories, result = [], depth = 0) => {
  if (!categories || !Array.isArray(categories) || depth >= MAX_NESTING_DEPTH) {
    return result;
  }
  
  categories.forEach(cat => {
    if (cat.name.trim()) {
      result.push(cat.name.trim());
      if (cat.children && cat.children.length > 0) {
        collectAllCategoryNames(cat.children, result, depth + 1);
      }
    }
  });
  
  return result;
};

// Build category hierarchy for backend (flat list with parent references)
const buildCategoryHierarchy = (categories, parentName = null, result = [], depth = 0) => {
  if (!categories || !Array.isArray(categories) || depth >= MAX_NESTING_DEPTH) {
    return result;
  }
  
  categories.forEach(cat => {
    if (cat.name.trim()) {
      result.push({
        name: cat.name.trim(),
        parent: parentName,
        depth: depth
      });
      if (cat.children && cat.children.length > 0) {
        buildCategoryHierarchy(cat.children, cat.name.trim(), result, depth + 1);
      }
    }
  });
  
  return result;
};

// Collect all category IDs that have children (for auto-expanding)
const collectExpandableIds = (categories, result = new Set(), depth = 0) => {
  if (!categories || !Array.isArray(categories) || depth >= MAX_NESTING_DEPTH) {
    return result;
  }
  
  categories.forEach(cat => {
    if (cat.children && cat.children.length > 0) {
      result.add(cat.id);
      collectExpandableIds(cat.children, result, depth + 1);
    }
  });
  
  return result;
};

const ChartOfAccountsEditor = ({ userId, userEmail, tenantName: propTenantName, tenantSlug: propTenantSlug }) => {
  const [sectionStructure, setSectionStructure] = useState(getDefaultSectionStructure());
  const [configName, setConfigName] = useState('');
  const [configType, setConfigType] = useState('income_statement');
  const [visibility, setVisibility] = useState('company'); // 'private' or 'company'
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [expandedSections, setExpandedSections] = useState(new Set(PL_SECTIONS.map(s => s.key)));
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  // eslint-disable-next-line no-unused-vars
  const [existingConfig, setExistingConfig] = useState(null);
  
  // Tenant info from props (comes from AccountOverview)
  const tenantName = propTenantName || '';
  const tenantSlug = propTenantSlug || '';
  
  // Auto-select private visibility if no tenant is linked
  useEffect(() => {
    if (!tenantName && visibility === 'company') {
      setVisibility('private');
    }
  }, [tenantName, visibility]);
  
  // Multi-config state
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Fetch all saved configurations for the user
  const fetchSavedConfigs = useCallback(async () => {
    if (!userEmail) return;
    
    setLoadingConfigs(true);
    try {
      // Get current session for JWT token
      await supabase.auth.getSession();
      
      const response = await fetch(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/tenant-config/list/${encodeURIComponent(userEmail)}`,
        {
          credentials: 'include',
          headers: {
            'x-api-key': import.meta.env.VITE_API_KEY,
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch configurations');
      }
      
      const data = await response.json();
      setSavedConfigs(data.configs || []);
      
    } catch (err) {
      if (err.name === 'SessionExpiredError') return;
      logger.error('Error fetching configurations:', err);
    } finally {
      setLoadingConfigs(false);
    }
  }, [userEmail]);

  useEffect(() => {
    fetchSavedConfigs();
  }, [fetchSavedConfigs]);

  // Load a specific config for editing
  const loadConfigForEditing = (config) => {
    // Check if user can edit this config
    if (!config.can_edit) {
      setMessage({ type: 'error', text: 'You do not have permission to edit this configuration' });
      return;
    }
    
    setSelectedConfigId(config.id);
    setIsCreatingNew(false);
    setExistingConfig(config);
    setConfigName(config.config_name || '');
    setVisibility(config.visibility || 'company');
    
    const loadedType = config.config_type || 'income_statement';
    const validType = loadedType === 'income_statement' ? 'income_statement' : 'income_statement';
    setConfigType(validType);
    
    // Parse template_structure if exists
    const templateStructure = config.template_structure 
      ? (typeof config.template_structure === 'string' 
          ? JSON.parse(config.template_structure) 
          : config.template_structure)
      : null;
    
    if (templateStructure && Object.keys(templateStructure).length > 0) {
      // Convert stored structure to editor format (with nested children support)
      const editorStructure = {};
      
      PL_SECTIONS.forEach(section => {
        const stored = templateStructure[section.key];
        if (stored && stored.subcategories) {
          editorStructure[section.key] = {
            name: stored.name || section.name,
            categories: convertStoredToEditor(stored.subcategories, section.key)
          };
        } else if (stored) {
          editorStructure[section.key] = {
            name: stored.name || section.name,
            // No stored subcategories means the section itself is the mapping category.
            // Represent that as an empty categories list in the editor to avoid creating
            // the invalid shape: Section -> [Section]
            categories: []
          };
        } else {
          editorStructure[section.key] = {
            name: section.name,
            categories: section.defaultCategories.map((cat, idx) => ({
              id: `${section.key}-${idx}-${Date.now()}`,
              name: cat,
              children: []
            }))
          };
        }
      });
      
      BELOW_EBITDA_SECTIONS.forEach(section => {
        const stored = templateStructure[section.key];
        editorStructure[section.key] = {
          name: stored?.name || section.name
        };
      });
      
      setSectionStructure(editorStructure);
      
      // Auto-expand all categories with children
      const expandableIds = new Set();
      PL_SECTIONS.forEach(section => {
        if (editorStructure[section.key]?.categories) {
          collectExpandableIds(editorStructure[section.key].categories, expandableIds);
        }
      });
      setExpandedCategories(expandableIds);
    } else {
      // Fallback: try to parse from old categories format
      setSectionStructure(getDefaultSectionStructure());
      setExpandedCategories(new Set());
    }
    
    setMessage({ type: 'info', text: `Editing: ${config.config_name || 'Configuration'}` });
  };

  // Start creating a new configuration
  const startNewConfig = () => {
    setSelectedConfigId(null);
    setIsCreatingNew(true);
    setExistingConfig(null);
    setConfigName('');
    setConfigType('income_statement');
    setVisibility('company'); // Default to company-wide
    setSectionStructure(getDefaultSectionStructure());
    setExpandedCategories(new Set());
    setMessage({ type: '', text: '' });
  };

  // Delete a configuration
  const handleDeleteConfig = async (configId) => {
    setDeleting(true);
    try {
      // Get current session for JWT token
      await supabase.auth.getSession();
      
      const response = await fetch(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/tenant-config/${configId}?user_email=${encodeURIComponent(userEmail)}`,
        {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'x-api-key': import.meta.env.VITE_API_KEY,
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to delete configuration');
      }
      
      setMessage({ type: 'success', text: 'Configuration deleted successfully' });
      setDeleteConfirm(null);
      
      await fetchSavedConfigs();
      
      if (selectedConfigId === configId) {
        startNewConfig();
      }
      
    } catch (err) {
      if (err.name === 'SessionExpiredError') return;
      logger.error('Error deleting config:', err);
      setMessage({ type: 'error', text: err.message || 'Failed to delete configuration' });
    } finally {
      setDeleting(false);
    }
  };

  // Add a category to a section (top level)
  const addCategoryToSection = (sectionKey) => {
    setSectionStructure(prev => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        categories: [
          ...(prev[sectionKey].categories || []),
          { id: generateId(), name: '', children: [] }
        ]
      }
    }));
  };

  // Recursively update a category in the tree
  const updateCategoryInTree = (categories, targetId, updateFn) => {
    return categories.map(cat => {
      if (cat.id === targetId) {
        return updateFn(cat);
      }
      if (cat.children && cat.children.length > 0) {
        return {
          ...cat,
          children: updateCategoryInTree(cat.children, targetId, updateFn)
        };
      }
      return cat;
    });
  };

  // Recursively remove a category from the tree
  const removeCategoryFromTree = (categories, targetId) => {
    return categories.filter(cat => {
      if (cat.id === targetId) {
        return false;
      }
      if (cat.children && cat.children.length > 0) {
        cat.children = removeCategoryFromTree(cat.children, targetId);
      }
      return true;
    });
  };

  // Calculate depth of a category in the tree
  const getCategoryDepth = (categories, targetId, currentDepth = 0) => {
    for (const cat of categories) {
      if (cat.id === targetId) {
        return currentDepth;
      }
      if (cat.children && cat.children.length > 0) {
        const foundDepth = getCategoryDepth(cat.children, targetId, currentDepth + 1);
        if (foundDepth !== -1) {
          return foundDepth;
        }
      }
    }
    return -1;
  };

  // Update a category name
  const updateCategoryName = (sectionKey, categoryId, newName) => {
    setSectionStructure(prev => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        categories: updateCategoryInTree(prev[sectionKey].categories, categoryId, cat => ({
          ...cat,
          name: newName
        }))
      }
    }));
  };

  // Add a subcategory to an existing category
  const addSubcategory = (sectionKey, parentId) => {
    const sectionData = sectionStructure[sectionKey];
    const depth = getCategoryDepth(sectionData.categories, parentId);
    
    if (depth >= MAX_NESTING_DEPTH - 1) {
      setMessage({ type: 'error', text: `Maximum nesting depth (${MAX_NESTING_DEPTH}) reached` });
      return;
    }
    
    setSectionStructure(prev => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        categories: updateCategoryInTree(prev[sectionKey].categories, parentId, cat => ({
          ...cat,
          children: [
            ...(cat.children || []),
            { id: generateId(), name: '', children: [] }
          ]
        }))
      }
    }));
    
    // Auto-expand the parent category
    setExpandedCategories(prev => new Set([...prev, parentId]));
  };

  // Remove a category from a section
  const removeCategory = (sectionKey, categoryId) => {
    setSectionStructure(prev => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        categories: removeCategoryFromTree(prev[sectionKey].categories, categoryId)
      }
    }));
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionKey)) {
        next.delete(sectionKey);
      } else {
        next.add(sectionKey);
      }
      return next;
    });
  };

  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!tenantName) {
      setMessage({ type: 'error', text: 'Please set up your company first using the panel above' });
      return;
    }

    // Prevent invalid/ambiguous state: a section has exactly one category with the same name as the section.
    // Example: Revenue -> [Revenue]. If you want only "Revenue", leave Revenue's categories empty so the
    // section name itself is used as the mapping category.
    for (const section of PL_SECTIONS) {
      const sectionData = sectionStructure[section.key];
      const parentName = (sectionData?.name || section.name || '').trim();
      const validCategories = (sectionData?.categories || []).filter(c => c?.name?.trim());
      if (
        parentName &&
        validCategories.length === 1 &&
        String(validCategories[0].name).trim().toLowerCase() === parentName.toLowerCase()
      ) {
        setMessage({
          type: 'error',
          text: `"${parentName}" cannot contain a single category also named "${parentName}". Remove that category (leave the section empty) or rename it.`,
        });
        return;
      }
    }

    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      
      if (!authUser) {
        throw new Error('Not authenticated');
      }

      // Use slug from props, or generate from tenant name
      const slug = tenantSlug || tenantName.toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');

      // Convert section structure to template_structure format for backend
      // Now with nested subcategories support
      const templateStructure = {};
      
      PL_SECTIONS.forEach(section => {
        const sectionData = sectionStructure[section.key];
        const validCategories = (sectionData?.categories || []).filter(c => c.name.trim());
        
        if (validCategories.length > 0) {
          templateStructure[section.key] = {
            name: sectionData.name || section.name,
            subcategories: convertEditorToStorage(validCategories)
          };
        } else {
          // Default to section name as single category
          templateStructure[section.key] = {
            name: sectionData?.name || section.name
          };
        }
      });
      
      BELOW_EBITDA_SECTIONS.forEach(section => {
        const sectionData = sectionStructure[section.key];
        templateStructure[section.key] = {
          name: sectionData?.name || section.name
        };
      });

      // Build flat categories list for backward compatibility
      const allCategories = [];
      PL_SECTIONS.forEach(section => {
        const sectionData = sectionStructure[section.key];
        collectAllCategoryNames(sectionData?.categories || [], allCategories);
      });

      // Build category hierarchy for backend (flat list with parent references)
      const categoryHierarchy = [];
      PL_SECTIONS.forEach(section => {
        const sectionData = sectionStructure[section.key];
        buildCategoryHierarchy(sectionData?.categories || [], null, categoryHierarchy);
      });

      const requestBody = {
        user_email: userEmail,
        external_auth_id: authUser.id,
        tenant_name: tenantName,
        tenant_slug: slug,
        config_type: configType,
        config_name: configName.trim() || '',
        categories: allCategories,
        llm_guidelines: '',  // Will be generated by backend
        template_structure: templateStructure,
        frontend_config: {
          sectionStructure: sectionStructure,  // Store full editor state
          categoryHierarchy: categoryHierarchy  // Include hierarchy for backend processing
        },
        visibility: visibility  // 'private' or 'company'
      };

      if (selectedConfigId && !isCreatingNew) {
        requestBody.config_id = selectedConfigId;
      }

      // Get current session for JWT token
      await supabase.auth.getSession();
      
      const response = await fetch(`${import.meta.env.VITE_BACKEND_BASE_URL}/tenant-config/save`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': import.meta.env.VITE_API_KEY,
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to save configuration');
      }

      const result = await response.json();
      
      setExistingConfig(result.config);
      setSelectedConfigId(result.config?.id || null);
      setIsCreatingNew(false);
      
      const actionText = requestBody.config_id ? 'updated' : 'created';
      setMessage({ type: 'success', text: `Configuration ${actionText} successfully!` });
      
      await fetchSavedConfigs();

    } catch (err) {
      if (err.name === 'SessionExpiredError') return;
      logger.error('Error saving config:', err);
      setMessage({ type: 'error', text: err.message || 'Failed to save configuration' });
    } finally {
      setSaving(false);
    }
  };

  // Recursive category row component
  const renderCategoryRow = (category, sectionKey, depth = 0) => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expandedCategories.has(category.id);
    const canAddChildren = depth < MAX_NESTING_DEPTH - 1;
    
    return (
      <div key={category.id} className="coa-pl-category-tree">
        <div className={`coa-pl-category-row coa-pl-depth-${depth}`}>
          {depth > 0 && (
            <div className="coa-pl-indent" style={{ width: `${depth * 24}px` }}>
              <CornerDownRight size={14} className="coa-pl-indent-icon" />
            </div>
          )}
          {hasChildren ? (
            <button
              className="coa-pl-expand-btn"
              onClick={() => toggleCategoryExpansion(category.id)}
              type="button"
            >
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          ) : (
            <div className="coa-pl-expand-placeholder" />
          )}
          <input
            type="text"
            value={category.name}
            onChange={(e) => updateCategoryName(sectionKey, category.id, e.target.value)}
            placeholder={depth === 0 ? 'Category name...' : 'Subcategory name...'}
            className="coa-pl-category-input"
          />
          {canAddChildren && (
            <button
              className="coa-pl-add-sub-btn"
              onClick={() => addSubcategory(sectionKey, category.id)}
              title="Add subcategory"
              type="button"
            >
              <Plus size={14} />
            </button>
          )}
          <button
            className="coa-pl-remove-btn"
            onClick={() => removeCategory(sectionKey, category.id)}
            title="Remove category"
            type="button"
          >
            <Trash2 size={14} />
          </button>
        </div>
        {hasChildren && isExpanded && (
          <div className="coa-pl-children">
            {category.children.map(child => renderCategoryRow(child, sectionKey, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  // Count all categories including nested
  const countAllCategories = (categories) => {
    let count = 0;
    categories?.forEach(cat => {
      if (cat.name.trim()) {
        count++;
        if (cat.children && cat.children.length > 0) {
          count += countAllCategories(cat.children);
        }
      }
    });
    return count;
  };

  // Render a P&L section with its categories
  const renderPLSection = (section) => {
    const sectionData = sectionStructure[section.key] || { categories: [] };
    const isExpanded = expandedSections.has(section.key);
    const categoryCount = countAllCategories(sectionData.categories);

    return (
      <div key={section.key} className="coa-pl-section">
        <div 
          className="coa-pl-section-header"
          onClick={() => toggleSection(section.key)}
        >
          <div className="coa-pl-section-toggle">
            {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </div>
          <div className="coa-pl-section-info">
            <div className="coa-pl-section-name">{section.name}</div>
            <div className="coa-pl-section-desc">{section.description}</div>
          </div>
          <div className="coa-pl-section-badge">
            {categoryCount} {categoryCount === 1 ? 'category' : 'categories'}
          </div>
        </div>
        
        {isExpanded && (
          <div className="coa-pl-section-content">
            <div className="coa-pl-categories">
              {(sectionData.categories || []).map(category => 
                renderCategoryRow(category, section.key, 0)
              )}
            </div>
            <button
              className="coa-pl-add-btn"
              onClick={() => addCategoryToSection(section.key)}
              type="button"
            >
              <Plus size={14} />
              Add Category to {section.name}
            </button>
          </div>
        )}
      </div>
    );
  };

  // Render below-EBITDA sections (display only - slots determined at runtime)
  const renderBelowEBITDASections = () => {
    return (
      <div className="coa-below-ebitda">
        <div className="coa-below-ebitda-header">
          <Layers size={16} />
          <span>Below EBITDA (Fixed Sections)</span>
        </div>
        <div className="coa-below-ebitda-grid">
          {BELOW_EBITDA_SECTIONS.map(section => (
            <div key={section.key} className="coa-below-ebitda-item">
              <span className="coa-below-ebitda-name">{section.name}</span>
            </div>
          ))}
        </div>
        <div className="coa-below-ebitda-note">
          These sections are automatically included in every P&L template.
        </div>
      </div>
    );
  };

  return (
    <div className={`coa-editor border-beam-container ${saving ? 'loading' : ''}`}>
      <div className="border-beam"></div>
      <div className="coa-header">
        <h3>Chart of Accounts</h3>
        <p className="coa-description">Define your P&L categories by section (supports up to {MAX_NESTING_DEPTH} levels of nesting)</p>
      </div>

      {message.text && (
        <div className={`coa-message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="coa-left-panel">
        {/* Saved Configurations Section */}
        <div className="coa-saved-configs">
          <div className="coa-saved-header">
            <h4>
              <FileText size={16} />
              Saved Configurations
            </h4>
            <button
              className="coa-reset-btn"
              onClick={startNewConfig}
              title="Reset editor"
              type="button"
            >
              <RotateCcw size={16} />
            </button>
          </div>

          {loadingConfigs ? (
            <div className="coa-configs-skeleton">
              {[140, 180, 115].map((w, i) => (
                <div key={i} className="coa-config-skeleton-item">
                  <div className="coa-skeleton-content">
                    <div className="coa-skeleton-line" style={{ width: w }} />
                    <div className="coa-skeleton-line coa-skeleton-line-sm" style={{ width: w * 0.6 }} />
                  </div>
                  <div className="coa-skeleton-actions">
                    <div className="coa-skeleton-btn" />
                    <div className="coa-skeleton-btn" />
                  </div>
                </div>
              ))}
            </div>
          ) : savedConfigs.length > 0 ? (
            <>
              {/* Company-wide configs */}
              {savedConfigs.filter(c => c.visibility === 'company').length > 0 && (
                <div className="coa-configs-group">
                  <div className="coa-configs-group-header">
                    <Globe size={14} />
                    <span>Company Configurations</span>
                  </div>
                  <div className="coa-configs-list">
                    {savedConfigs.filter(c => c.visibility === 'company').map((config) => (
                      <div
                        key={config.id}
                        className={`coa-config-item ${selectedConfigId === config.id ? 'selected' : ''}`}
                      >
                        <div className="coa-config-item-content" onClick={() => loadConfigForEditing(config)}>
                          <div className="coa-config-item-name">
                            {config.config_name || 'Income Statement Config'}
                          </div>
                          <div className="coa-config-item-meta">
                            <span className="coa-config-type-badge">
                              {config.config_type === 'income_statement' ? 'P&L' : 'BS'}
                            </span>
                            <span className="coa-config-count">{config.categories?.length || 0} categories</span>
                            {config.created_by_email && (
                              <>
                                <span className="coa-config-separator">|</span>
                                <span className="coa-config-creator">
                                  Made by: {config.is_owner ? 'you' : config.created_by_email.split('@')[0]}
                                </span>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="coa-config-item-actions">
                          {config.can_edit && (
                            <button
                              className="coa-config-edit-btn"
                              onClick={() => loadConfigForEditing(config)}
                              title="Edit configuration"
                              type="button"
                            >
                              <Edit3 size={14} />
                            </button>
                          )}

                          {config.can_delete && (
                            deleteConfirm === config.id ? (
                              <div className="coa-delete-confirm">
                                <span className="coa-delete-confirm-text">Delete?</span>
                                <button
                                  className="coa-delete-yes-btn"
                                  onClick={() => handleDeleteConfig(config.id)}
                                  disabled={deleting}
                                  type="button"
                                >
                                  {deleting ? '...' : 'Yes'}
                                </button>
                                <button
                                  className="coa-delete-no-btn"
                                  onClick={() => setDeleteConfirm(null)}
                                  type="button"
                                >
                                  No
                                </button>
                              </div>
                            ) : (
                              <button
                                className="coa-config-delete-btn"
                                onClick={() => setDeleteConfirm(config.id)}
                                title="Delete configuration"
                                type="button"
                              >
                                <Trash2 size={14} />
                              </button>
                            )
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Private configs */}
              {savedConfigs.filter(c => c.visibility === 'private').length > 0 && (
                <div className="coa-configs-group">
                  <div className="coa-configs-group-header private">
                    <Lock size={14} />
                    <span>Your Private Configurations</span>
                  </div>
                  <div className="coa-configs-list">
                    {savedConfigs.filter(c => c.visibility === 'private').map((config) => (
                      <div
                        key={config.id}
                        className={`coa-config-item private ${selectedConfigId === config.id ? 'selected' : ''}`}
                      >
                        <div className="coa-config-item-content" onClick={() => loadConfigForEditing(config)}>
                          <div className="coa-config-item-name">
                            {config.config_name || 'Income Statement Config'}
                          </div>
                          <div className="coa-config-item-meta">
                            <span className="coa-config-type-badge private">
                              {config.config_type === 'income_statement' ? 'P&L' : 'BS'}
                            </span>
                            <span className="coa-config-count">{config.categories?.length || 0} categories</span>
                          </div>
                        </div>

                        <div className="coa-config-item-actions">
                          <button
                            className="coa-config-edit-btn"
                            onClick={() => loadConfigForEditing(config)}
                            title="Edit configuration"
                            type="button"
                          >
                            <Edit3 size={14} />
                          </button>

                          {deleteConfirm === config.id ? (
                            <div className="coa-delete-confirm">
                              <span className="coa-delete-confirm-text">Delete?</span>
                              <button
                                className="coa-delete-yes-btn"
                                onClick={() => handleDeleteConfig(config.id)}
                                disabled={deleting}
                                type="button"
                              >
                                {deleting ? '...' : 'Yes'}
                              </button>
                              <button
                                className="coa-delete-no-btn"
                                onClick={() => setDeleteConfirm(null)}
                                type="button"
                              >
                                No
                              </button>
                            </div>
                          ) : (
                            <button
                              className="coa-config-delete-btn"
                              onClick={() => setDeleteConfirm(config.id)}
                              title="Delete configuration"
                              type="button"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="coa-configs-empty">
              <FileText size={24} />
              <p>No saved configurations yet</p>
              <span>Create your first Chart of Accounts below</span>
            </div>
          )}
        </div>
        <button
          className="coa-new-btn"
          onClick={startNewConfig}
          type="button"
        >
          <PlusCircle size={16} />
          New Configuration
        </button>
      </div>

      <div className="coa-right-panel">
        {/* Configuration Editor Section */}
        <div className="coa-editor-section">
          <div className="coa-editor-section-header">
            <h4>{isCreatingNew || !selectedConfigId ? 'New Configuration' : 'Edit Configuration'}</h4>
          </div>

          <div className="coa-config-section">
            <div className="coa-form-group">
              <label htmlFor="configName">Configuration Name</label>
              <input
                id="configName"
                type="text"
                value={configName}
                onChange={(e) => setConfigName(e.target.value)}
                placeholder="e.g., Restaurant P&L 2025"
                className="coa-input"
              />
            </div>

            <div className="coa-form-group">
              <label htmlFor="configType">Statement Type</label>
              <select
                id="configType"
                value={configType}
                onChange={(e) => setConfigType(e.target.value)}
                className="coa-select"
              >
                <option value="income_statement">Income Statement</option>
              </select>
            </div>

            <div className="coa-form-group">
              <label>Visibility</label>
              <div className="coa-visibility-selector">
                <button
                  type="button"
                  className={`coa-visibility-option ${visibility === 'company' ? 'selected' : ''} ${!tenantName ? 'disabled' : ''}`}
                  onClick={() => tenantName && setVisibility('company')}
                  disabled={!tenantName}
                  title={!tenantName ? 'Set up your company first to create company-wide configs' : ''}
                >
                  <Globe size={16} />
                  <div className="coa-visibility-text">
                    <span className="coa-visibility-title">Company-Wide</span>
                    <span className="coa-visibility-desc">
                      {tenantName ? 'All team members can use' : 'Set up company first'}
                    </span>
                  </div>
                </button>
                <button
                  type="button"
                  className={`coa-visibility-option ${visibility === 'private' ? 'selected' : ''}`}
                  onClick={() => setVisibility('private')}
                >
                  <Lock size={16} />
                  <div className="coa-visibility-text">
                    <span className="coa-visibility-title">Private</span>
                    <span className="coa-visibility-desc">Only you can see</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* P&L Sections */}
        <div className="coa-pl-sections">
          <div className="coa-pl-sections-header">
            <h4>P&L Structure</h4>
            <p>Add your categories under each section. Click + on a category to add nested subcategories.</p>
          </div>

          {PL_SECTIONS.map(section => renderPLSection(section))}

          {renderBelowEBITDASections()}
        </div>

        <div className="coa-actions">
          <button
            className="coa-save-btn"
            onClick={handleSave}
            disabled={saving}
            type="button"
          >
            {saving ? (
              <>
                <div className="coa-spinner"></div>
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                Save Configuration
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChartOfAccountsEditor;
